import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FileText, Shield, Cookie, ChevronLeft } from 'lucide-react';
import { policyContent } from '../components/Policy/policyContent';
import SEO from '../components/SEO/SEO'; // Adjust path if needed
import PolicyMenu from '../components/Policy/PolicyMenu';

const PolicyPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || location.state?.activeTab || 'terms');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && policyContent[tab]) {
            setActiveTab(tab);
        } else if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        }
    }, [searchParams, location.state]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleGoBack = () => {
        navigate(-1);
    };

    const policyTabs = [
        { id: 'terms', label: 'Terms', icon: FileText },
        { id: 'privacy', label: 'Privacy', icon: Shield },
        { id: 'cookies', label: 'Cookies', icon: Cookie },
    ];

    const current = policyContent[activeTab];

    // Logo Component
    const LogoSection = () => (
        <div className="flex items-center">
            <img
                src="/logos/Wryven-Feather-Color.png?v=1.0"
                alt="Wryven Logo"
                className="w-16 h-16 sm:w-16 sm:h-16 rounded-2xl flex-shrink-0 object-cover"
                loading="lazy"
            />
            <div>
                <h1 className="wryven-font text-4xl text-gray-900">Wryven</h1>
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <>
                <SEO page="policy" />
                <div className="min-h-screen bg-white flex flex-col">
                    {/* Mobile Header */}
                    <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
                        <div className="px-4 pt-4 pb-4">
                            <button
                                onClick={handleGoBack}
                                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition mb-4"
                            >
                                <ChevronLeft size={24} className="text-black" />
                            </button>
                            <LogoSection />
                        </div>

                        {/* Policy Menu Below Logo */}
                        <PolicyMenu
                            activeTab={activeTab}
                            onTabChange={(tab) => {
                                setActiveTab(tab);
                                navigate(`/policy?tab=${tab}`, { replace: true });
                            }}
                            isMobile={true}
                        />
                    </div>

                    {/* Mobile Content - Direct display based on activeTab */}
                    <div className="flex-1 overflow-y-auto px-4 pt-3">
                        <div className="max-w-2xl mx-auto">
                            <div className="mb-3">
                                <h2 className="text-xl font-semibold text-black mb-1">{current.title}</h2>
                                <p className="text-sm text-black text-justify">{current.subtitle}</p>
                            </div>

                            <p className="font-semibold text-[#6b8f3a] text-xs mb-4 text-justify">{current.tagline}</p>
                            {current.lastUpdated && (
                                <p className="text-xs text-gray-500 mb-4">Last Updated: {current.lastUpdated}</p>
                            )}


                            <div className="space-y-6">
                                {current.sections.map((section, idx) => (
                                    <div key={idx}>
                                        <h3 className="text-base font-semibold text-black mb-2">{section.title}</h3>
                                        {section.content && (
                                            <p className="text-sm text-black leading-relaxed whitespace-pre-line mb-4 text-justify">
                                                {section.content}
                                            </p>
                                        )}
                                        {section.subsections && section.subsections.length > 0 && (
                                            <div className="space-y-4 ml-0">
                                                {section.subsections.map((subsection, subIdx) => (
                                                    <div key={subIdx} className="pl-4">
                                                        <h4 className="text-sm font-semibold text-black mb-2">{subsection.subtitle}</h4>
                                                        <p className="text-sm text-black leading-relaxed whitespace-pre-line text-justify">
                                                            {subsection.content}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="mt-12 pt-6 border-t border-gray-200 text-center pb-6">
                                <p className="text-xs text-black">© 2026 Wryven. All rights reserved.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }


    // Desktop View - Centered Layout
    return (
        <>
            <SEO page="policy" />
            <div className="min-h-screen bg-white flex justify-center items-start">
                <div className="flex w-full max-w-6xl mx-auto">
                    {/* Desktop Sidebar - Logo and Navigation */}
                    <div className="w-80 sticky top-0 h-screen overflow-y-auto border-r border-gray-200">
                        <div className="p-6">
                            <div className="mb-8">
                                <LogoSection />
                            </div>

                            {/* Sidebar Navigation */}
                            <div className="space-y-2">
                                {policyTabs.map((tab) => {
                                    const Icon = tab.icon;
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => {
                                                setActiveTab(tab.id);
                                                navigate(`/policy?tab=${tab.id}`, { replace: true });
                                            }}
                                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition text-base ${isActive
                                                ? 'bg-gray-100 text-black'
                                                : 'text-gray-600 hover:text-black hover:bg-gray-50'
                                                }`}
                                        >
                                            <Icon size={20} />
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Sidebar Footer */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <p className="text-xs text-black text-center">© 2026 Wryven</p>
                            </div>
                        </div>
                    </div>

                    {/* Desktop Content Area */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="px-8 py-8">
                            {/* Title Section */}
                            <div className="mb-6">
                                <h2 className="text-4xl font-semibold text-black mb-1">{current.title}</h2>
                                <p className="text-lg text-black mb-3">{current.subtitle}</p>
                                <p className="font-semibold text-[#6b8f3a] text-sm">{current.tagline}</p>
                                {current.lastUpdated && (
                                    <p className="text-sm text-gray-500 mt-1">Last Updated: {current.lastUpdated}</p>
                                )}
                            </div>

                            {/* Content */}
                            <div className="space-y-4 max-w-2xl">
                                {current.sections.map((section, idx) => (
                                    <div key={idx}>
                                        <h3 className="text-xl font-semibold text-black mb-3">{section.title}</h3>
                                        {section.content && (
                                            <p className="text-base text-black leading-relaxed whitespace-pre-line mb-4">{section.content}</p>
                                        )}
                                        {section.subsections && section.subsections.length > 0 && (
                                            <div className="space-y-5 ml-0">
                                                {section.subsections.map((subsection, subIdx) => (
                                                    <div key={subIdx} className="border-l-3 border-blue-500 pl-4">
                                                        <h4 className="text-base font-semibold text-black mb-2">{subsection.subtitle}</h4>
                                                        <p className="text-base text-black leading-relaxed whitespace-pre-line">{subsection.content}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Footer */}
                            <div className="mt-8 pt-4 border-t border-gray-200">
                                <p className="text-sm text-center text-black">© 2026 Wryven. All rights reserved.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PolicyPage;